import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '@aimsos/hooks';
import { Button, Filters, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus, SearchX, ShieldCheck, TriangleAlert } from 'lucide-react';

import Can from '../components/guards/Can';
import PeopleAccessHeader from '../components/PeopleAccessHeader';
import RolesGroupedList from '../components/roles/RolesGroupedList';
import { PERMISSIONS } from '../constants/permissions';
import { useOwnPeopleAccessHeader } from '../hooks/useOwnPeopleAccessHeader';
import { useRoles } from '../hooks/useRoles';

/** Pause after the last keystroke before the list is filtered. */
const SEARCH_DEBOUNCE_MS = 300;

/** Module-scope constants: `Filters` is memoised. The bar has no dropdowns yet, only the search. */
const NO_FILTERS = Object.freeze([]);
const VIEW_TOGGLE_HIDDEN = Object.freeze({ hidden: true });

/**
 * Roles tab: System Roles and Custom Roles with their member counts, a search that filters as you
 * type (in the same filter bar as Members, ready for more filters) and New role.
 *
 * @param {object} props
 * @param {string} props.basePath Where the host mounted People & Access (see `getBasePath`).
 */
function RolesPage({ basePath }) {
  useOwnPeopleAccessHeader();
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { groups, roles, isLoading, error, search, setSearch } = useRoles();
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  useEffect(() => {
    setSearch(debouncedQuery.trim());
  }, [debouncedQuery, setSearch]);

  const handleChange = useCallback((event) => setQuery(event.target.value), []);
  // Enter applies the search at once, without waiting for the pause.
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') setSearch(query.trim());
    },
    [query, setSearch],
  );
  const handleClear = useCallback(() => {
    setQuery('');
    setSearch('');
  }, [setSearch]);
  const searchProps = useMemo(
    () => ({
      value: query,
      placeholder: t('roles.list.searchPlaceholder'),
      'aria-label': t('roles.list.searchAria'),
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onClear: handleClear,
    }),
    [query, t, handleChange, handleKeyDown, handleClear],
  );
  const filtersI18n = useMemo(() => ({ clearFilters: t('roles.list.clearFilters') }), [t]);
  const handleOpen = useCallback((roleId) => navigate(`${basePath}/roles/${roleId}`), [navigate, basePath]);
  const handleNew = useCallback(() => navigate(`${basePath}/roles/new`), [navigate, basePath]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-3" data-testid="roles-loading">
          <Skeleton height="96px" borderRadius="12px" />
          <Skeleton height="96px" borderRadius="12px" />
        </div>
      );
    }
    if (error) {
      return (
        <StatusMessage
          icon={TriangleAlert}
          title={t('roles.list.loadError')}
          subtitle={t('roles.list.loadErrorSubtitle')}
        />
      );
    }
    if (roles.length === 0) {
      return search ? (
        <StatusMessage icon={SearchX} title={t('roles.list.noResults.title')} subtitle={t('roles.list.noResults.subtitle')} />
      ) : (
        <StatusMessage icon={ShieldCheck} title={t('roles.list.empty.title')} subtitle={t('roles.list.empty.subtitle')} />
      );
    }
    return <RolesGroupedList groups={groups} onOpen={handleOpen} />;
  };

  // A new element every render would defeat the memo of `PeopleAccessHeader`.
  const headerActions = useMemo(
    () => (
      <Can permission={PERMISSIONS.ROLES_MANAGE}>
        <Button leftIcon={Plus} onClick={handleNew}>
          {t('roles.list.newRole')}
        </Button>
      </Can>
    ),
    [handleNew, t],
  );

  return (
    <div className="flex flex-col gap-6" data-testid="page-roles">
      <PeopleAccessHeader actions={headerActions} />
      <Filters
        searchProps={searchProps}
        filters={NO_FILTERS}
        showAllFilters={false}
        viewToggle={VIEW_TOGGLE_HIDDEN}
        onClearAll={handleClear}
        i18n={filtersI18n}
      />
      {renderBody()}
    </div>
  );
}

export default RolesPage;
